import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardText, CardFooter } from 'reactstrap';
import PropTypes from 'prop-types';
import { isPaymentConnected, disconnectPayment } from '../../axios/oauth/oauth';
import { useAuthStore } from '../../zustand-store/auth.store';
import DeleteModal from './DeleteModal';

const ACTION_REQUIRED_AND_DISCONNECT_REASONS = [
  'requirements.past_due',
  'action_required.requested_capabilities',
  'rejected.incomplete_verification'
];

const UNDER_REVIEW_DISCONNECT_ONLY_REASONS = ['requirements.pending_verification', 'under_review'];

const REJECTED_DISCONNECT_ONLY_REASONS = [
  'rejected.listed',
  'rejected.fraud',
  'rejected.platform_fraud',
  'rejected.terms_of_service',
  'rejected.platform_terms_of_service',
  'rejected.other',
  'rejected.platform_other'
];

const StripeIntegrationCard = ({
  title,
  description,
  icon,
  integartionFlag,
  onClick,
  isSquareIntegrated,
  setIsSquareIntegarted,
  setIntegrationFlag,
  onRequirementsDueChange,
  setDisabledReason,
  disabledReason
}) => {
  const [loader, setLoader] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const locationId = useAuthStore((state) => state.locationId);
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [isStripeIntegrated, setIsStripeIntegrated] = useState(false);
  const [isSquareActive, setIsSquareActive] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [requirementsDue, setRequirementsDue] = useState(null);
  const [stripeStatus, setStripeStatus] = useState('not_connected');

  useEffect(() => {
    checkStripeConnected(locationId);
  }, [integartionFlag, locationId, isSquareIntegrated]);

  useEffect(() => {
    if (onRequirementsDueChange) {
      onRequirementsDueChange(requirementsDue);
    }
  }, [requirementsDue]);

  const getStripeStatus = (disabledReason, stripeActive) => {
    if (stripeActive) return 'connected';
    if (!disabledReason) return 'not_connected';
    if (ACTION_REQUIRED_AND_DISCONNECT_REASONS.includes(disabledReason))
      return 'action_required_and_disconnect';
    if (UNDER_REVIEW_DISCONNECT_ONLY_REASONS.includes(disabledReason))
      return 'under_review_disconnect_only';
    if (REJECTED_DISCONNECT_ONLY_REASONS.includes(disabledReason))
      return 'rejected_disconnect_only';
    return 'not_connected';
  };

  const checkStripeConnected = async (locationId) => {
    try {
      const response = await isPaymentConnected(locationId);
      console.log(response);
      if (response.status === 200) {
        const integrations = response?.data?.data || [];

        const stripeIntegration = integrations.find((item) => item.type === 'Stripe');

        const squareIntegration = integrations.find((item) => item.type === 'Square-Pay');
        setPaymentId(stripeIntegration?.id);
        setRequirementsDue(stripeIntegration?.providerData?.requirementsDue || null);
        const currentDisabledReason = stripeIntegration?.providerData?.disabledReason || null;
        setDisabledReason(currentDisabledReason);

        const stripeActive =
          stripeIntegration?.isActive &&
          stripeIntegration?.providerData?.chargesEnabled &&
          stripeIntegration?.providerData?.detailsSubmitted === true;
        const squareActive = squareIntegration?.isActive === true;
        setStripeAccountId(stripeIntegration?.providerData?.stripeAccountId || '');
        setIsStripeIntegrated(stripeActive);
        setIsSquareActive(squareActive);
        setStripeStatus(getStripeStatus(currentDisabledReason, stripeActive));
      }
    } catch (error) {
      setIsStripeIntegrated(false);
      setIsSquareActive(false);
      setStripeStatus('not_connected');
    } finally {
      setLoader(false);
    }
  };

  const disconnect = async (paymentId, locationId) => {
    setDisconnecting(true);
    try {
      let response = await disconnectPayment(paymentId, locationId, 'Stripe');
      console.log(response);
      if (response.status == 200 || response.status == 201) {
        setDisconnecting(false);
        setIntegrationFlag(!integartionFlag);
        setDisconnectModalOpen(false);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCardClick = () => {
    if (!isSquareActive) {
      onClick(setLoader);
    }
  };

  const isClickDisabled = isSquareActive && stripeStatus === 'not_connected';

  const shouldShowActionRequiredButton = stripeStatus === 'action_required_and_disconnect';
  const shouldShowDisconnectButton =
    stripeStatus === 'connected' ||
    stripeStatus === 'action_required_and_disconnect' ||
    stripeStatus === 'under_review_disconnect_only' ||
    stripeStatus === 'rejected_disconnect_only';

  return (
    <>
      <Card
        style={{
          border: '1px solid',
          color: '#EFF2F7',
          cursor: isClickDisabled ? 'not-allowed' : 'pointer',
          height: 250,
          opacity: isClickDisabled ? 0.6 : 1
        }}
        onClick={() => {
          if (!isStripeIntegrated) {
            handleCardClick();
          }
        }}
        id="stripe-integration-card">
        <CardBody>
          <div className="d-flex justify-content-between">
            <img style={{ width: '30px', height: '30px' }} src={icon} alt="" />

            {loader ? (
              <span className="text-warning">Loading...</span>
            ) : stripeStatus === 'connected' ? (
              <span
                className="bg-success text-white"
                style={{
                  fontSize: 13,
                  padding: 6,
                  paddingLeft: 16,
                  paddingRight: 16,
                  borderRadius: 100
                }}>
                Connected
              </span>
            ) : stripeStatus === 'action_required_and_disconnect' ? (
              <span className="text-warning">Action Required</span>
            ) : stripeStatus === 'under_review_disconnect_only' ? (
              <span className="text-warning">Account Under Verification Via Stripe</span>
            ) : stripeStatus === 'rejected_disconnect_only' ? (
              <span className="text-danger">Rejected</span>
            ) : !isSquareActive ? (
              <span className="text-primary">Connect</span>
            ) : null}
          </div>
          <p
            style={{ fontSize: '13px', fontWeight: '700', color: '#000', marginTop: 14 }}
            className="mt-2">
            {title}
          </p>
          <CardText style={{ color: '#000', fontSize: '13px', fontWeight: '400', marginTop: 8 }}>
            {description}
          </CardText>
          {((requirementsDue && requirementsDue.length > 0) || disabledReason) && (
            <CardText
              style={{ color: '#DC3545', fontSize: '12px', fontWeight: '500', marginTop: 8 }}
              title={requirementsDue?.join('\n') || disabledReason}>
              {requirementsDue && requirementsDue.length > 0 && (
                <span>
                  {requirementsDue.length} requirement{requirementsDue.length !== 1 ? 's' : ''}{' '}
                  pending
                </span>
              )}
              {disabledReason && <span className="d-block mt-1">{disabledReason}</span>}
            </CardText>
          )}
          {isStripeIntegrated && (
            <CardText style={{ color: '#74788D' }}>
              <span
                style={{ textDecorationLine: 'underline' }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`https://dashboard.stripe.com/${stripeAccountId}`, '_blank');
                }}>
                {`https://dashboard.stripe.com/${stripeAccountId}`}
              </span>
            </CardText>
          )}
        </CardBody>
        <CardFooter
          style={{ minHeight: '48px' }}
          className="d-flex align-items-center justify-content-end gap-2 py-2">
          {shouldShowActionRequiredButton && (
            <button
              className="btn btn-sm btn-warning"
              onClick={(e) => {
                e.stopPropagation();
                onClick(setLoader);
              }}>
              Action Required
            </button>
          )}
          {shouldShowDisconnectButton && (
            <>
              {disconnecting ? (
                <span className="text-warning" style={{ fontSize: 13 }}>
                  Loading...
                </span>
              ) : (
                <span
                  className="text-danger"
                  style={{ cursor: 'pointer', fontSize: 13 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDisconnectModalOpen(true);
                  }}>
                  Disconnect
                </span>
              )}
            </>
          )}
        </CardFooter>
      </Card>
      <DeleteModal
        show={disconnectModalOpen}
        isDeleting={disconnecting}
        heading="If this account is disconnected, you will no longer be able to initiate refunds for any payments collected on it."
        componentName="Stripe"
        buttonTitle="Disconnect"
        onDeleteClick={() => disconnect(paymentId, locationId)}
        onCloseClick={() => setDisconnectModalOpen(false)}
      />
    </>
  );
};

StripeIntegrationCard.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  isIntegrated: PropTypes.string,
  icon: PropTypes.any,
  onClick: PropTypes.any,
  integartionFlag: PropTypes.any,
  isSquareIntegrated: PropTypes.any,
  setIsSquareIntegarted: PropTypes.any,
  setIntegrationFlag: PropTypes.any,
  onRequirementsDueChange: PropTypes.any,
  setDisabledReason: PropTypes.any,
  disabledReason: PropTypes.any
};

export default StripeIntegrationCard;
